import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Briefcase, Eye, EyeOff, KeyRound } from "lucide-react";

const DEMO_EMAIL = "hr@newmoonmedia.co.za";
const DEMO_PW = "Employer@2026";

export default function EmployerLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const doLogin = async (em, pw) => {
        setLoading(true);
        try {
            const u = await login(em, pw);
            if (u.role !== "employer") {
                toast.error("This account is not an employer account.");
                return;
            }
            toast.success(`Welcome, ${u.name}`);
            navigate("/employer");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const useDemo = () => {
        setEmail(DEMO_EMAIL);
        setPassword(DEMO_PW);
        doLogin(DEMO_EMAIL, DEMO_PW);
    };

    return (
        <div className="bg-white min-h-screen" data-testid="employer-login-page">
            <Navbar />
            <main className="pt-28 pb-20">
                <section className="max-w-md mx-auto px-6">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3 text-center">EMPLOYER PORTAL</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-center mb-3">
                        Sign in as an <span className="brand-gradient-text">employer</span>
                    </h1>
                    <p className="text-center text-sm text-gray-600 mb-8">
                        Access aggregate wellness insights for your workforce — real-time HR triage flags across every kiosk scan your team completes.
                    </p>

                    <div className="card-soft p-7">
                        <form
                            onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }}
                            className="space-y-4"
                            data-testid="employer-login-form"
                        >
                            <div>
                                <label className="text-xs font-mono tracking-[0.18em] text-gray-500">EMAIL</label>
                                <input
                                    required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                    placeholder="hr@yourcompany.co.za"
                                    data-testid="employer-login-email"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-mono tracking-[0.18em] text-gray-500">PASSWORD</label>
                                <div className="relative mt-1">
                                    <input
                                        required type={show ? "text" : "password"} value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        placeholder="••••••••"
                                        data-testid="employer-login-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShow((v) => !v)}
                                        className="absolute inset-y-0 right-3 grid place-items-center text-gray-400"
                                    >
                                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit" disabled={loading}
                                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid="employer-login-submit"
                            >
                                <Briefcase className="w-4 h-4" /> {loading ? "Signing in…" : "Sign in as employer"}
                            </button>
                        </form>

                        <div className="mt-6 rounded-xl border border-dashed border-[var(--medx-orange)]/50 bg-orange-50/60 p-4">
                            <div className="flex items-start gap-3">
                                <KeyRound className="w-4 h-4 text-[var(--medx-red)] mt-0.5 shrink-0" />
                                <div className="text-xs text-gray-700 leading-relaxed">
                                    <div className="font-semibold text-gray-900 mb-1">Demo employer account</div>
                                    <div className="font-mono text-[11px]">
                                        <span className="text-gray-500">email:</span> <span data-testid="employer-demo-email">{DEMO_EMAIL}</span><br />
                                        <span className="text-gray-500">password:</span> <span data-testid="employer-demo-password">{DEMO_PW}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={useDemo}
                                        disabled={loading}
                                        className="mt-3 text-xs font-semibold text-[var(--medx-red)] hover:underline disabled:opacity-50"
                                        data-testid="employer-demo-signin"
                                    >
                                        Sign in as demo employer →
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 text-center text-xs text-gray-500">
                            Need a workforce dashboard for your company? <Link to="/contact" className="text-[var(--medx-red)] font-semibold hover:underline">Talk to us</Link>
                        </div>
                        <div className="text-center mt-2">
                            <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-[var(--medx-red)]" data-testid="employer-forgot-password-link">
                                Forgot your password?
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
